import type {
  SingleFactorSignInInput,
  VerificationCodeInput,
} from '@sunrise-ui/api/cerebro';
import { type TokenInput, type TokenOutput } from '@sunrise-ui/api/cerebro';
import { useAuthStore, useSnackbar } from '@sunrise-ui/primitives';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../constants/QueryKeys';
import { tokensApi, useAuthMutation, versionsApi } from '../utils/api';
import { assertParameterIsNotUndefinedOrNull } from '../utils/error';

const { versionsKey } = QueryKeys.versions;

export const usePasswordLogin = () => {
  const queryClient = useQueryClient();
  const { setAuthData } = useAuthStore();
  const { showSnackbar } = useSnackbar();

  return useMutation<TokenOutput, unknown, TokenInput>({
    async mutationFn(tokenInput) {
      const response = await tokensApi.apiLoginForAccessTokenTokensApiPost({
        tokenInput,
      });
      return response.data;
    },
    async onSuccess(data) {
      await setAuthData({
        authToken: data.access_token,
        refreshToken: data.refresh_token,
        currentUserId: data.user_id,
      });
      await queryClient.invalidateQueries();
    },
    async onError() {
      showSnackbar({ text: 'Login failed. Please try again.', type: 'error' });
    },
  });
};

export const useSingleFactorSignIn = () => {
  const { showSnackbar } = useSnackbar();

  return useMutation({
    async mutationFn(singleFactorSignInInput: SingleFactorSignInInput) {
      await tokensApi.singleFactorSignInTokensSignInSingleFactorPost({
        singleFactorSignInInput,
      });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to send email. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useSingleFactorValidateCode = () => {
  const queryClient = useQueryClient();
  const { setAuthData } = useAuthStore();

  return useMutation({
    async mutationFn(verificationCodeInput: VerificationCodeInput) {
      const response =
        await tokensApi.singleFactorCodeValidationTokensSignInSingleFactorValidateCodePost(
          {
            verificationCodeInput,
          },
        );
      return response.data;
    },
    async onSuccess(data) {
      await setAuthData({
        authToken: data.access_token,
        refreshToken: data.refresh_token,
        currentUserId: data.user_id,
      });
      await queryClient.invalidateQueries();
    },
  });
};

export const useAcceptTerms = () => {
  const { showSnackbar } = useSnackbar();
  const { setAuthData, currentUserId } = useAuthStore();
  const queryClient = useQueryClient();

  return useAuthMutation({
    async mutationFn() {
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');
      const response = await tokensApi.acceptTermsTokensAcceptTermsPost({
        acceptTermsInput: {
          user_id: currentUserId,
        },
      });
      return response.data;
    },
    async onSuccess(data) {
      await setAuthData({
        authToken: data.access_token,
        refreshToken: data.refresh_token,
        currentUserId: data.user_id,
      });
      await queryClient.invalidateQueries();
    },
    async onError() {
      showSnackbar({
        text: 'Failed to accept terms. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useCheckVersion = (enabled: boolean) =>
  useQuery({
    async queryFn() {
      const response = await versionsApi.listActiveVersionsVersionsGet();
      return response.data;
    },
    queryKey: [versionsKey],
    staleTime: 3600000,
    enabled,
  });
